import React from 'react';

import type { CliveChipVariant } from '~/components/AiChat/CliveChip/CliveChip';
import { CliveChip } from '~/components/AiChat/CliveChip/CliveChip';
import type { CliveMood } from '~/components/Clive/cliveRegistry';

// Generic status chip props
export interface StatusChipProps<T extends string> {
  status: T | null | undefined;
  variant?: CliveChipVariant;
}

// Generic status configuration type
export type StatusConfig<T extends string> = {
  [K in T]: {
    iconType: CliveMood;
    content: string;
  };
};

// Generic status chip factory function
export function createStatusChipComponent<T extends string>(
  statusConfig: StatusConfig<T>,
  validStatuses: readonly T[]
): React.FC<StatusChipProps<T>> {
  const StatusChipComponent: React.FC<StatusChipProps<T>> = ({ status, variant }) => {
    // Handle null/undefined status
    if (!status) return null;

    // Ensure status is a valid enum value
    if (!validStatuses.includes(status)) return null;

    const config = statusConfig[status];
    if (!config) return null;

    const { iconType, content } = config;
    return <CliveChip variant={variant} iconType={iconType} content={content} />;
  };

  StatusChipComponent.displayName = 'StatusChipComponent';
  return StatusChipComponent;
}
